import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { ChartData } from '../../types';
import { useChartTheme } from './ChartTheme';

interface SimplePieChartProps {
  data: ChartData;
  title?: string;
  height?: number;
  variant?: 'pie' | 'donut';
  className?: string;
}

export const SimplePieChart: React.FC<SimplePieChartProps> = ({
  data,
  title,
  height = 350,
  variant = 'pie',
  className,
}) => {
  const chartTheme = useChartTheme();
  const colors = chartTheme.colors.default;

  const dataset = data.datasets[0];
  if (!dataset || !dataset.data || dataset.data.length === 0) return null;

  const total = dataset.data.reduce((sum, value) => sum + value, 0);
  if (total === 0) return null;

  // Calculate angles for each slice
  const pieData = data.labels.map((label, index) => {
    const value = dataset.data[index] || 0;
    return {
      label,
      value,
      percentage: (value / total) * 100,
      color: dataset.backgroundColor?.[index] || colors[index % colors.length],
    };
  });

  // Calculate cumulative angles
  let cumulativeAngle = 0;
  const slices = pieData.map((item) => {
    const startAngle = cumulativeAngle;
    const angle = (item.value / total) * 360;
    cumulativeAngle += angle;
    return {
      ...item,
      startAngle,
      endAngle: cumulativeAngle,
      angle,
    };
  });

  // SVG dimensions
  const svgSize = Math.min(height - 80, 300);
  const center = svgSize / 2;
  const radius = svgSize / 2 - 10;
  const innerRadius = variant === 'donut' ? radius * 0.6 : 0;

  // Convert polar to cartesian coordinates
  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const createPath = (slice: (typeof slices)[0]) => {
    const { startAngle, endAngle } = slice;
    const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';

    if (variant === 'donut') {
      const outerStart = polarToCartesian(center, center, radius, endAngle);
      const outerEnd = polarToCartesian(center, center, radius, startAngle);
      const innerStart = polarToCartesian(center, center, innerRadius, endAngle);
      const innerEnd = polarToCartesian(center, center, innerRadius, startAngle);

      return [
        'M',
        outerStart.x,
        outerStart.y,
        'A',
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        outerEnd.x,
        outerEnd.y,
        'L',
        innerEnd.x,
        innerEnd.y,
        'A',
        innerRadius,
        innerRadius,
        0,
        largeArcFlag,
        1,
        innerStart.x,
        innerStart.y,
        'Z',
      ].join(' ');
    } else {
      if (slice.angle >= 360) {
        return `M ${center} ${center - radius} A ${radius} ${radius} 0 1 0 ${center} ${center + radius} A ${radius} ${radius} 0 1 0 ${center} ${center - radius}`;
      }

      const start = polarToCartesian(center, center, radius, endAngle);
      const end = polarToCartesian(center, center, radius, startAngle);

      return [
        'M',
        center,
        center,
        'L',
        start.x,
        start.y,
        'A',
        radius,
        radius,
        0,
        largeArcFlag,
        0,
        end.x,
        end.y,
        'Z',
      ].join(' ');
    }
  };

  return (
    <Box className={className}>
      {title && (
        <Typography variant="h6" gutterBottom textAlign="center">
          {title}
        </Typography>
      )}

      <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
        <svg width={svgSize} height={svgSize}>
          {slices.map((slice, index) => (
            <Tooltip
              key={index}
              title={
                <Box>
                  <Typography variant="body2">{slice.label}</Typography>
                  <Typography variant="body2">
                    {slice.value} ({slice.percentage.toFixed(1)}%)
                  </Typography>
                </Box>
              }
              arrow
            >
              <path
                d={createPath(slice)}
                fill={slice.color}
                stroke="#fff"
                strokeWidth="2"
                style={{
                  cursor: 'pointer',
                  filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.1))',
                  transition: 'transform 0.2s ease, opacity 0.2s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.85';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              />
            </Tooltip>
          ))}

          {/* Center text for donut chart */}
          {variant === 'donut' && (
            <g>
              <text
                x={center}
                y={center - 10}
                textAnchor="middle"
                fontSize="24"
                fontWeight="bold"
                fill={chartTheme.text.primary}
              >
                {total}
              </text>
              <text
                x={center}
                y={center + 10}
                textAnchor="middle"
                fontSize="14"
                fill={chartTheme.text.secondary}
              >
                Total
              </text>
            </g>
          )}
        </svg>

        <Box display="flex" flexWrap="wrap" gap={2} justifyContent="center">
          {slices.map((slice, index) => (
            <Box key={index} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: slice.color,
                }}
              />
              <Typography variant="body2">
                {slice.label}: {slice.percentage.toFixed(1)}%
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
